import { fromBaseRecord, fromBaseUuid } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { readItemField } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonInt, phpJsonString } from './php-json-values';

const tableTypes = new Set(['random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table']);

/** Saved range rows use the same reader for raw, wrapped and lean values. */
export function randomTableRangesFromRows(raw: readonly Row[]): Row[] {
    return raw.filter(row => phpJsonInt(readItemField(row, 'random_table_range_deleted')) === 0)
        .sort((a, b) => phpJsonInt(readItemField(b, 'roll_range_end')) - phpJsonInt(readItemField(a, 'roll_range_end'))
            || phpJsonInt(readItemField(b, 'random_table_range_i')) - phpJsonInt(readItemField(a, 'random_table_range_i')))
        .flatMap(row => {
            const begin = readItemField(row, 'roll_range_begin'), end = readItemField(row, 'roll_range_end');
            if (begin === null || end === null) return [];
            const range: Row = { begin: phpJsonInt(begin), end: phpJsonInt(end) };
            for (const [out, column] of [['effect', 'roll_result_effect'], ['description', 'roll_result_effect_description']] as const) {
                const value = phpJsonString(readItemField(row, column)); if (value !== '') range[out] = value;
            }
            const attached = readItemField(row, 'item_id_attached'), type = readItemField(row, 'attached_type');
            if (fromBaseUuid(attached)) { range.attached = attached.toUpperCase(); if (typeof type === 'string' && type !== '') range.attached_type = type; }
            for (const [out, column] of [['qty_dice', 'roll_dice'], ['qty_die', 'roll_die'], ['qty_mod', 'roll_mod']] as const) {
                const value = phpJsonInt(readItemField(row, column)); if (value !== 0) range[out] = value;
            }
            return [range];
        });
}
export interface FromBaseRandomTableContext {
    rawDefinition: (id: string) => Row | null;
    definition: (id: string) => Row | null;
    /** Complete saved-range SELECT per table, including empty results. Each row
     * carries the joined live item's raw attached_type (not a computed outcome). */
    randomTableRows?: Readonly<Record<string, readonly Row[] | null>>;
}

/** Native range interning: sharing facts, end/index ordering, sparse columns,
 * bounded nested tables, and removal of stale/unshareable riding buckets. */
export function internFromBaseRandomTables(root: Row, catalog: Row, context: FromBaseRandomTableContext,
    store: (id: string, definition: Row, keepPl: boolean) => void): void {
    const pending = new Set<string>(), seen = new Set<string>();
    function collect(node: unknown): void {
        if (node === null || typeof node !== 'object') return;
        for (const [key, value] of Object.entries(node)) {
            if (key === 'roll_random_table') for (const entry of Array.isArray(value) ? value : [value]) {
                const id = readLayerListEntryUuid(entry); if (fromBaseUuid(id)) pending.add(id.toUpperCase());
            }
            collect(value);
        }
    }
    collect(root); for (const value of Object.values(catalog)) collect(value);
    let budget = 4000;
    function intern(id: string, depth: number): void {
        const key = id.toUpperCase(); if (seen.has(key) || depth > 3) return;
        seen.add(key);
        const entryKey = [id, key, key.toLowerCase()].find(k => fromBaseRecord(catalog[k])) ?? key;
        const drop = () => { for (const k of [id, key, key.toLowerCase()]) if (fromBaseRecord(catalog[k])) delete catalog[k].random_table_ranges; };
        const source = context.rawDefinition(key);
        if (!source || !tableTypes.has(phpJsonString(readItemField(source, 'item_type')))
            || phpJsonInt(readItemField(source, 'item_deleted')) !== 0 || phpJsonInt(readItemField(source, 'item_private')) !== 0) { drop(); return; }
        const raw = context.randomTableRows?.[key];
        if (raw === null) { drop(); return; } // The shared SELECT withheld a non-shareable bucket.
        if (!Array.isArray(raw)) { drop(); return; } // Recovered by the action's table read, before drawing.
        if (raw.some(row => !phpJsonInt(readItemField(row, 'random_table_range_deleted'))
            && phpJsonInt(readItemField(row, 'random_table_range_private')) !== 0)) { drop(); return; }
        const ranges = randomTableRangesFromRows(raw);
        if (ranges.length > budget) { drop(); return; }
        if (!fromBaseRecord(catalog[entryKey]) || !fromBaseRecord(catalog[entryKey].item_layer_types)
            || !Object.keys(catalog[entryKey].item_layer_types).length) {
            const definition = context.definition(key); if (!definition) { drop(); return; }
            const { catalog: _catalog, ...ownDefinition } = definition;
            store(entryKey, ownDefinition, true);
        }
        budget -= ranges.length;
        (catalog[entryKey] as Row).random_table_ranges = ranges;
        for (const range of ranges) if (typeof range.attached === 'string' && range.attached !== key && tableTypes.has(String(range.attached_type)))
            intern(range.attached, depth + 1);
    }
    for (const id of pending) intern(id, 0);
    for (const [key, row] of Object.entries(catalog)) if (fromBaseRecord(row) && !seen.has(key.toUpperCase())) delete row.random_table_ranges;
}
